import { useState, useMemo, useCallback } from 'react';
import { Printer, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

/* Custom time picker: Hour : Minute  AM/PM */
function TimePicker({ value, onChange, testId }) {
  // value stored as "HH:MM" 24-hr internally
  const parsed = useMemo(() => {
    if (!value) return { hr: '', min: '', ampm: 'AM' };
    const [h, m] = value.split(':').map(Number);
    return {
      hr: String(h % 12 || 12),
      min: String(m).padStart(2, '0'),
      ampm: h >= 12 ? 'PM' : 'AM',
    };
  }, [value]);

  const emit = useCallback((hr, min, ampm) => {
    if (!hr || min === '') return onChange('');
    let h24 = parseInt(hr, 10);
    if (ampm === 'PM' && h24 !== 12) h24 += 12;
    if (ampm === 'AM' && h24 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2, '0')}:${min}`);
  }, [onChange]);

  const selectClass = "h-8 rounded-md border border-slate-200 bg-white text-sm px-2 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none appearance-none";

  return (
    <div className="flex items-center gap-1.5 mt-1" data-testid={testId}>
      <select
        value={parsed.hr}
        onChange={(e) => emit(e.target.value, parsed.min || '00', parsed.ampm)}
        className={`${selectClass} w-16`}
        aria-label="Hour"
      >
        <option value="">Hr</option>
        {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-slate-400 font-bold">:</span>
      <select
        value={parsed.min}
        onChange={(e) => emit(parsed.hr || '12', e.target.value, parsed.ampm)}
        className={`${selectClass} w-16`}
        aria-label="Minute"
      >
        <option value="">Min</option>
        {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <select
        value={parsed.ampm}
        onChange={(e) => emit(parsed.hr || '12', parsed.min || '00', e.target.value)}
        className={`${selectClass} w-16`}
        aria-label="AM/PM"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

const INITIAL = {
  rstNo: '',
  party: '',
  vehicleNo: '',
  material: '',
  grossWt: '',
  tareWt: '',
  grossDate: new Date().toISOString().slice(0, 10),
  grossTime: '',
  tareDate: new Date().toISOString().slice(0, 10),
  tareTime: '',
};

export default function WeighBridgeSlip() {
  const [form, setForm] = useState(INITIAL);

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const netWt = useMemo(() => {
    const g = parseFloat(form.grossWt) || 0;
    const t = parseFloat(form.tareWt) || 0;
    return g > 0 && t > 0 ? g - t : 0;
  }, [form.grossWt, form.tareWt]);

  const fmtDate = (d) => (d ? d.split('-').reverse().join('-') : '');

  const fmtTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const handleReset = () => setForm(INITIAL);

  return (
    <div data-testid="weigh-bridge-slip">

      {/* ── Screen: Input Form ── */}
      <div className="wb-no-print">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Weigh Bridge Slip
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} data-testid="wb-reset" className="gap-1.5 text-slate-600 border-slate-300">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </Button>
            <Button size="sm" onClick={() => window.print()} data-testid="wb-print" className="gap-1.5 bg-amber-500 hover:bg-amber-600 text-white border-0">
              <Printer className="w-3.5 h-3.5" /> Print
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4 w-full max-w-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="wb-label">RST No</label>
              <Input data-testid="wb-rstno" value={form.rstNo} onChange={set('rstNo')} placeholder="797" className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <label className="wb-label">Vehicle No</label>
              <Input data-testid="wb-vehicle" value={form.vehicleNo} onChange={set('vehicleNo')} placeholder="AP39VB7619" className="mt-1 h-8 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="wb-label">Party</label>
              <Input data-testid="wb-party" value={form.party} onChange={set('party')} placeholder="Party name" className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <label className="wb-label">Material</label>
              <Input data-testid="wb-material" value={form.material} onChange={set('material')} placeholder="Silica Sand" className="mt-1 h-8 text-sm" />
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2">Gross Weight</p>
            <div>
              <label className="wb-label">Weight (kg)</label>
              <Input data-testid="wb-gross" value={form.grossWt} onChange={set('grossWt')} placeholder="36430" className="mt-1 h-8 text-sm" />
            </div>
            <div className="mt-2">
              <label className="wb-label">Date</label>
              <Input data-testid="wb-gross-date" type="date" value={form.grossDate} onChange={set('grossDate')} className="mt-1 h-8 text-sm" />
            </div>
            <div className="mt-2">
              <label className="wb-label">Time</label>
              <TimePicker testId="wb-gross-time" value={form.grossTime} onChange={(v) => setForm((p) => ({ ...p, grossTime: v }))} />
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2">Tare Weight</p>
            <div>
              <label className="wb-label">Weight (kg)</label>
              <Input data-testid="wb-tare" value={form.tareWt} onChange={set('tareWt')} placeholder="10450" className="mt-1 h-8 text-sm" />
            </div>
            <div className="mt-2">
              <label className="wb-label">Date</label>
              <Input data-testid="wb-tare-date" type="date" value={form.tareDate} onChange={set('tareDate')} className="mt-1 h-8 text-sm" />
            </div>
            <div className="mt-2">
              <label className="wb-label">Time</label>
              <TimePicker testId="wb-tare-time" value={form.tareTime} onChange={(v) => setForm((p) => ({ ...p, tareTime: v }))} />
            </div>
          </div>

          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Net Weight :</span>
            <span data-testid="wb-net" className="text-xl font-black text-slate-800" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {netWt > 0 ? `${netWt} kg` : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* ═══════ Print-only: Exact replica of the weigh bridge slip ═══════ */}
      <div id="wb-print-slip" className="wb-print-only">
        <div className="wb-slip">
          {/* Header — centered */}
          <div className="wb-hdr">
            <span className="wb-hdr-title">MJ WEIGH BRIDGE</span>
            <span className="wb-hdr-addr">RAJUPALEM, OZILI (M), TIRUPATHI</span>
          </div>

          {/* Info rows — RST NO + VEHICLE NO on same row, PARTY + MATERIAL on same row */}
          <table className="wb-tbl">
            <tbody>
              <tr>
                <td className="wb-l">RST NO</td>
                <td className="wb-c">:</td>
                <td className="wb-v">{form.rstNo}</td>
                <td className="wb-mid"></td>
                <td className="wb-lr">VEHICLE NO</td>
                <td className="wb-c">:</td>
                <td className="wb-vr">{form.vehicleNo}</td>
              </tr>
              <tr>
                <td className="wb-l">PARTY</td>
                <td className="wb-c">:</td>
                <td className="wb-v">{form.party}</td>
                <td className="wb-mid"></td>
                <td className="wb-lr">MATERIAL</td>
                <td className="wb-c">:</td>
                <td className="wb-vr">{form.material}</td>
              </tr>
            </tbody>
          </table>

          {/* Weight section with Date/Time */}
          <table className="wb-tbl wb-wt-tbl">
            <tbody>
              <tr>
                <td className="wb-l">GROSS wt</td>
                <td className="wb-c">:</td>
                <td className="wb-wv">{form.grossWt ? `${form.grossWt} kg` : ''}</td>
                <td className="wb-dl">Date</td>
                <td className="wb-dc">:</td>
                <td className="wb-dv">{fmtDate(form.grossDate)}</td>
                <td className="wb-dl">Time</td>
                <td className="wb-dc">:</td>
                <td className="wb-dv">{fmtTime(form.grossTime)}</td>
              </tr>
              <tr>
                <td className="wb-l">TARE wt</td>
                <td className="wb-c">:</td>
                <td className="wb-wv">{form.tareWt ? `${form.tareWt} kg` : ''}</td>
                <td className="wb-dl">Date</td>
                <td className="wb-dc">:</td>
                <td className="wb-dv">{fmtDate(form.tareDate)}</td>
                <td className="wb-dl">Time</td>
                <td className="wb-dc">:</td>
                <td className="wb-dv">{fmtTime(form.tareTime)}</td>
              </tr>
              <tr className="wb-net-row">
                <td className="wb-l">NET wt</td>
                <td className="wb-c">:</td>
                <td className="wb-wv" colSpan="7">{netWt > 0 ? `${netWt} kg` : ''}</td>
              </tr>
            </tbody>
          </table>

          {/* Operator Signature */}
          <div className="wb-sig">
            <span className="wb-sig-lbl">OPERATOR'S SIGNATURE:</span>
            <div className="wb-sig-line"></div>
          </div>
        </div>
      </div>

      <style>{`
        .wb-label {
          font-size: 10px; font-weight: 700; color: #b45309;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .wb-print-only { display: none; }
        .wb-no-print { display: block; }

        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          html, body { margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden; }
          #wb-print-slip, #wb-print-slip * { visibility: visible; }
          #wb-print-slip {
            position: fixed; left: 0; top: 0; width: 100%; height: 100%;
            display: flex !important; align-items: flex-start; justify-content: center;
            padding-top: 12mm;
          }
          .wb-print-only { display: flex !important; }
          .wb-no-print { display: none !important; }

          /* ── Slip container: landscape rectangle ── */
          .wb-slip {
            width: 88%;
            border: 2px solid #000;
            padding: 14px 20px 14px 20px;
            font-family: 'Courier New', Courier, monospace;
            color: #000;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Header */
          .wb-hdr {
            text-align: center;
            border-bottom: 1px solid #000;
            padding-bottom: 6px;
            margin-bottom: 8px;
          }
          .wb-hdr-title {
            display: block;
            font-size: 18px;
            font-weight: 900;
            letter-spacing: 3px;
          }
          .wb-hdr-addr {
            display: block;
            font-size: 9px;
            letter-spacing: 0.5px;
            margin-top: 1px;
          }

          /* Tables */
          .wb-tbl {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
            line-height: 2;
          }
          .wb-l { font-weight: 700; white-space: nowrap; padding-right: 4px; }
          .wb-c { width: 12px; text-align: center; }
          .wb-v { font-weight: 400; }
          .wb-mid { width: auto; }
          .wb-lr { font-weight: 700; white-space: nowrap; padding-right: 4px; text-align: right; }
          .wb-vr { font-weight: 400; white-space: nowrap; text-align: right; }

          /* Weight table */
          .wb-wt-tbl {
            margin-top: 6px;
            border-top: 1px dashed #000;
            padding-top: 4px;
          }
          .wb-wv { font-weight: 400; white-space: nowrap; padding-right: 20px; }
          .wb-dl { font-weight: 700; white-space: nowrap; padding-left: 8px; padding-right: 2px; }
          .wb-dc { width: 10px; text-align: center; }
          .wb-dv { font-weight: 400; white-space: nowrap; padding-right: 8px; }
          .wb-net-row td {
            font-weight: 900 !important;
            font-size: 12px;
            border-top: 1px dashed #000;
            padding-top: 2px;
          }

          /* Signature */
          .wb-sig {
            margin-top: 12px;
            border-top: 1px dashed #000;
            padding-top: 8px;
          }
          .wb-sig-lbl {
            font-size: 10px;
            font-weight: 700;
          }
          .wb-sig-line {
            margin-top: 28px;
            border-top: 1px solid #000;
            width: 40%;
          }
        }
      `}</style>
    </div>
  );
}
